import React, {useContext, useState, useEffect} from 'react';
import {FormattedMessage, injectIntl} from "react-intl";
import PropTypes from 'prop-types';

import {Formik, Form as FormikForm} from "formik";

import Grid from "@mui/material/Grid";

import FormBloc from "components/form/FormBloc";
import ActionsContainer from "components/containers/ActionsContainer";
import CancelButtonStyled from "components/button/CancelButtonStyled";
import SaveButton from "components/button/SaveButton";
import {ListContext} from "components/List/context/ListContext";

import NameField from "./components/NameField";
import SyliusCodeField from "./components/SyliusCodeField";
import MarketplaceCodeField from "./components/MarkeplaceCodeField";
import MethodTypeField from "./components/MethodTypeField";
import SkipReservedStepField from "./components/SkipReservedStepField";
import DefaultReturnMethodField from "./components/DefaultReturnMethodField";
import WaybillFields from "./components/WaybillFields";
import GoogleIconField from "./components/GoogleIconField";
import {ShippingMethodFormContext} from "./context/ShippingMethodFormContext";
import ReturnWaybillFields from "./components/ReturnWaybillFields";
import AdditionalDataFields from "./components/AdditionalDataFields";
import MetadataFields from "./components/MetadataFields";

import update from "api/shippingMethod/update";
import {create} from "api/shippingMethod/create";

import getValidationSchema from "./validationSchema";
import CarrierIconField from "./components/CarrierIconField";
import CarrierField from "./components/CarrierField";
import DeliveryModeField from "./components/DeliveryModeField";
import DeliveryDurationFields from "./components/DeliveryDurationFields";
import ReturnOnlyField from "./components/ReturnOnlyField";
import CheckoutNameField from "./components/CheckoutNameField";
import TrackingUrlField from "./components/TrackingUrlField";
import {SyliusCandidatesContext} from "views/shippingMethods/context/SyliusCandidatesContext";
import {applyMetadataToValues, toMetadataRows} from "./utils/metadata";
import {ShippingMethodsDiscr} from "utils/ShippingMethods";

ShippingMethodForm.propTypes = {
    refreshList: PropTypes.func.isRequired,
    onClose: PropTypes.func,
};
function ShippingMethodForm({refreshList, onClose = () => {}, ...props}) {
    const [isReturnWaybill, setIsReturnWaybill] = useState(false);
    const [isApi, setIsApi] = useState(false);
    const [isWaybill, setIsWaybill] = useState(false);

    const {
        loading,
        selectedItem,
        notFilteredListItems,
        closeCreateDialog,
    } = useContext(ListContext)

    // a Sylius method being configured: the creation form starts from what Sylius has
    const {prefill} = useContext(SyliusCandidatesContext);
    const candidate = selectedItem ? null : prefill;

    function close() {
        closeCreateDialog();
        onClose();
    }

    const [discr, setDiscr] = useState(selectedItem?.discr || '');

    const initialValues = {
        'name': selectedItem?.name || candidate?.name || '',
        'shopName': selectedItem?.shopName || candidate?.code || '',
        'checkoutName': selectedItem?.checkoutName || candidate?.name || '',
        'trackingUrl': selectedItem?.trackingUrl || candidate?.trackingUrl || '',
        'icon': selectedItem?.icon || null,
        'carrierIcon': selectedItem?.carrierIcon || null,
        'carrier': selectedItem?.carrier || null,
        'deliveryMode': selectedItem?.deliveryMode || candidate?.deliveryMode || '',
        // strings for the text inputs, turned back into integers on submit
        'minimumEstimatedDeliveryDuration': toDurationInput(selectedItem?.minimumEstimatedDeliveryDuration ?? candidate?.minimumEstimatedDeliveryDuration),
        'maximumEstimatedDeliveryDuration': toDurationInput(selectedItem?.maximumEstimatedDeliveryDuration ?? candidate?.maximumEstimatedDeliveryDuration),
        // created from Sylius: keep its enabled flag, the push must not switch on a method it had off
        ...(candidate ? {'active': candidate.enabled} : {}),
        'returnOnly': selectedItem?.returnOnly || false,
        'shippingMethodShopName': selectedItem?.shippingMethodShopName || '',
        'discr': selectedItem?.discr || '',
        'skipReservedStep': selectedItem?.skipReservedStep || false,
        'isDefaultReturnMethod': selectedItem?.isDefaultReturnMethod || false,
        'senderName': selectedItem?.senderName || null,
        'contactMailAddress': selectedItem?.contactMailAddress || null,
        'contactPhoneNumber': selectedItem?.contactPhoneNumber || null,
        'senderAddress': selectedItem?.senderAddress || null,
        'senderCity': selectedItem?.senderCity || null,
        'senderCityZipCode': selectedItem?.zipCode || null,
        'senderCountryCode': selectedItem?.codeCountry || null,
        'contractNumber': selectedItem?.contractNumber || null,
        'wsPassword': selectedItem?.wsPassword || null,
        'userId': ShippingMethodsDiscr.DPD === selectedItem?.discr ? selectedItem.userId : null,
        'customerNumber': ShippingMethodsDiscr.DPD === selectedItem?.discr ? selectedItem.customerNumber : null,
        'customerCenterNumber': ShippingMethodsDiscr.DPD === selectedItem?.discr ? selectedItem.customerCenterNumber : null,
        'secureService': ShippingMethodsDiscr.DPD === selectedItem?.discr ? selectedItem.secureService || false : false,
        'printConfig': (selectedItem?.printConfig && !Array.isArray(selectedItem.printConfig)) ? selectedItem.printConfig : {
            'returnShippingMethodOverride': null,
            'printReturnWaybill': false
        },
        'additionalData': {
            sandbox: selectedItem?.additionalData?.sandbox || false,
            customerNumber: selectedItem?.additionalData?.customerNumber || null, // nshift
            carrierServiceId: selectedItem?.additionalData?.carrierServiceId || null, // nshift
            returnCarrierServiceId: selectedItem?.additionalData?.returnCarrierServiceId || null, // nshift
            api_url: selectedItem?.additionalData?.api_url || null, // this field and below are for geodis
            api_username: selectedItem?.additionalData?.api_username || null,
            api_key: selectedItem?.additionalData?.api_key || null,
            agency_code: selectedItem?.additionalData?.agency_code || null,
            client_code: selectedItem?.additionalData?.client_code || null,
            product_code: selectedItem?.additionalData?.product_code || null,
            sender_name: selectedItem?.additionalData?.sender_name || null,
            sender_country_code: selectedItem?.additionalData?.sender_country_code || null,
            sender_post_code : selectedItem?.additionalData?.sender_post_code || null,
            sender_city: selectedItem?.additionalData?.sender_city || null,
        },
        // free metadata, stored in additionalData alongside the fields above
        'metadata': toMetadataRows(selectedItem?.additionalData),
    };

    // when editing, check if the fields are filled in to show them in the form
    useEffect(() => {
        if (selectedItem === null) {
            return;
        }

        // show waybill fields
        ['senderName', 'contactMailAddress', 'contactPhoneNumber', 'senderAddress', 'senderCity'].some((fieldName) => {
            if (selectedItem && selectedItem[fieldName] && selectedItem[fieldName] !== null) {
                setIsWaybill(true);
                return true;
            }
        });

        // show api fields
        ['contractNumber', 'wsPassword'].some((fieldName) => {
            if (selectedItem && selectedItem[fieldName] && selectedItem[fieldName] !== null) {
                setIsApi(true);
                return true;
            }
        })

        // show return waybill fields
        if (selectedItem?.printConfig?.returnShippingMethodOverride && selectedItem.printConfig.returnShippingMethodOverride !== '') {
            setIsReturnWaybill(true);
        }
    }, [selectedItem]);

    function setConditionalValues(values) {
        if (!isWaybill) {
            values.senderName = null;
            values.senderAddress = null;
            values.senderCity = null;
            values.senderCityZipCode = null;
            values.senderCountryCode = null;
            values.contactMailAddress = null;
            values.contactPhoneNumber = null;
            values.contractNumber = null;
            values.wsPassword = null;
            values.customerNumber = null;
            values.customerCenterNumber = null;
        }

        if (!isApi) {
            values.contractNumber = null;
            values.wsPassword = null;
        }

        if (!isReturnWaybill) {
            delete values.printConfig.returnShippingMethodOverride;
            delete values.printConfig.printReturnWaybill;
        }

        // the API names these zipCode and codeCountry, as read in initialValues
        values.zipCode = values.senderCityZipCode;
        values.codeCountry = values.senderCountryCode;

        values.checkoutName = values.checkoutName?.trim() || null;
        values.trackingUrl = values.trackingUrl?.trim() || null;
        values.minimumEstimatedDeliveryDuration = toDuration(values.minimumEstimatedDeliveryDuration);
        values.maximumEstimatedDeliveryDuration = toDuration(values.maximumEstimatedDeliveryDuration);

        if (values.customerNumber !== null) values.customerNumber = parseInt(values.customerNumber, 10);
        if (values.customerCenterNumber !== null) values.customerCenterNumber = parseInt(values.customerCenterNumber, 10);

        return values;
    }

    return (
        <ShippingMethodFormContext.Provider value={{
            isApi,
            setIsApi,
            isWaybill,
            setIsWaybill,
            isReturnWaybill,
            setIsReturnWaybill,
            discr,
            setDiscr,
        }}>
            <Formik
                initialValues={initialValues}
                validationSchema={getValidationSchema(isWaybill, isReturnWaybill, notFilteredListItems, !!selectedItem, discr)}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnBlur={true}
                validateOnMount={true}
                onSubmit={(values, {setSubmitting}) => {
                    let promise;

                    values = applyMetadataToValues(setConditionalValues(values));

                    if (selectedItem) {
                        promise = update(selectedItem, values);
                    } else {
                        promise = create(values);
                    }

                    promise.then(() => {
                        close();
                        refreshList()
                    }).finally(() => setSubmitting(false));
                }}
            >
                {({
                      isSubmitting,
                      isValid,
                }) => (
                    <FormikForm>
                        <Grid container rowSpacing={6}>
                            <FormBloc
                                titleId={selectedItem ? 'shippingMethods.form.title.edit' : 'shippingMethods.form.title.create'}
                                subtitleId='shippingMethods.form.subtitle'
                            >
                                <Grid container rowSpacing={3}>
                                    <Grid item xs={12}>
                                        <NameField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CheckoutNameField />
                                    </Grid>
                                    <Grid item container columnSpacing={2}>
                                        <Grid item xs={6}>
                                            <SyliusCodeField />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MarketplaceCodeField />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GoogleIconField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SkipReservedStepField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DefaultReturnMethodField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ReturnOnlyField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MethodTypeField />
                                    </Grid>
                                    <Grid item container columnSpacing={2}>
                                        <Grid item xs={6}>
                                            <CarrierField />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <DeliveryModeField />
                                        </Grid>
                                    </Grid>
                                    <DeliveryDurationFields />
                                    <Grid item xs={12}>
                                        <TrackingUrlField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CarrierIconField />
                                    </Grid>
                                    <WaybillFields />
                                    <Grid item container>
                                        <AdditionalDataFields />
                                    </Grid>
                                    <ReturnWaybillFields />
                                </Grid>
                            </FormBloc>
                            <FormBloc
                                titleId='shippingMethods.form.metadata.title'
                                subtitleId='shippingMethods.form.metadata.subtitle'
                            >
                                <MetadataFields />
                            </FormBloc>
                            <Grid item xs={12}>
                                <ActionsContainer>
                                    <CancelButtonStyled
                                        disabled={isSubmitting}
                                        onClick={close}
                                    >
                                        <FormattedMessage id="list.button.cancel" />
                                    </CancelButtonStyled>
                                    <SaveButton
                                        type={"submit"}
                                        disabled={loading || !isValid || isSubmitting}
                                    >
                                        {selectedItem ? (
                                            <FormattedMessage id="shippingMethods.form.button.edit" />
                                        ) : (
                                            <FormattedMessage id="shippingMethods.form.button.create" />
                                        )}
                                    </SaveButton>
                                </ActionsContainer>
                            </Grid>
                        </Grid>
                    </FormikForm>
                )}
            </Formik>
        </ShippingMethodFormContext.Provider>
    );
}

function toDurationInput(duration) {
    return duration === null || duration === undefined ? '' : String(duration);
}

function toDuration(input) {
    return input === null || input === undefined || String(input).trim() === '' ? null : parseInt(input, 10);
}

export default injectIntl(ShippingMethodForm);
